import { chain, difference, intersection } from "underscore";
import { BrandliveEvent, RegistrationStep, RegistrationStepType, RegistrationTypes, Session, SessionPreview, StepsCompleted } from "../types/working-model";

/**
 *
 * @param eventBundle 
 * @param paymentRequired 
 */
export function hasEventAccess(eventBundle: BrandliveEvent | null, validSessions: Array<any>, finishedRegistering: boolean, userVerified: boolean, firesidesHostSessions: number[]): boolean {

	if (!eventBundle) { return false; }
	if (!eventBundle.registration_on) { return true; } // No registration required
	if (!validSessions || validSessions.length === 0) { return false; } // No session access

	const emailConfirmationRequired = !!eventBundle?.registration_settings?.registrationConfirmation || firesidesHostSessions.length > 0;

	return (finishedRegistering && !emailConfirmationRequired) || // Registration complete, no email confirmation required
		(emailConfirmationRequired && userVerified && finishedRegistering); // Registration complete, requires email and has confirmed email (usercode)
}

export function hasSessionAccess(
	eventBundle: BrandliveEvent | null,
	session: Session | SessionPreview,
	validSessions: Array<any>,
	paidSessions?: Array<string>,
	isModerator?: boolean,
): boolean {

	if (!eventBundle) { return false; }
	if (!eventBundle.registration_on || isModerator) { return true; } // No registration required

	if (paidSessions && paidSessions.includes(session.uuid)) {
		return true;
	}

	const ticketingStep = eventBundle?.registration_steps?.find(step => step.type == RegistrationStepType.ticketing && step.isOn);
	let sessionHasTicket = false;

	if (ticketingStep) {
		const sessionTickets = ticketingStep.ticketing?.find(ticket => ticket.session_uuids.indexOf(session.uuid) >= 0) || [];

		if (sessionTickets) {
			sessionHasTicket = true;
		}
	}

	if (sessionHasTicket) { // Already checked if paid session contains uuid
		return false;
	}

	return validSessions.includes(session.session) || eventBundle.registration_settings?.type !== RegistrationTypes.gated;
}

/**
 * Reconcile the required registration questions with the steps completed.
 * Returns a list of unanswered required questions
 */
export const findIncompleteRequiredQuestions = (requiredQuestions: number[] | null, stepsCompleted?: StepsCompleted): number[] => {
	// Nothing to do here
	if (!requiredQuestions || requiredQuestions.length === 0) {
		return [];
	}

	// get the question ids for all steps completed
	const answeredQuestions: number[] = chain(stepsCompleted ?? {}).values().flatten().uniq().value();

	return difference(requiredQuestions, answeredQuestions);
};

/**
 * Given a list of incomplete questions ids,
 * Get the first registration step that is turned on and has that question.
 */
export const findFirstIncompleteStep = (registrationSteps: RegistrationStep[], requiredQuestions: number[] | null, stepsCompleted: StepsCompleted): RegistrationStepType | null => {
	// For allowing null stepsCompleted for backwards compatibility.
	// The general registration step should be completed before this function is ran.
	if (!registrationSteps?.length || !requiredQuestions?.length || !stepsCompleted) { return null; }

	const requiredAndIncomplete = findIncompleteRequiredQuestions(requiredQuestions, stepsCompleted);

	if (!requiredAndIncomplete.length) { return null; }

	for (const step of registrationSteps) {
		// Skip inactive steps
		if (!step.isOn) {
			continue;
		}

		const questionIdsForStep = step.questions?.map(q => q.registration_question) ?? [];
		const hasOverlap = intersection(questionIdsForStep, requiredAndIncomplete).length > 0;

		if (hasOverlap) {
			return step.type;
		}
	}
	return null;
};
